/* ============================================================
   PHEROZ Scrollytelling Engine
   Canvas-based frame animation synced to scroll position
   Vanilla JS — Optimized for maximum fluidity with Lerp smoothing
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Config ---------- */
  const HERO_SELECTOR = '.pheroz-hero';
  const CANVAS_SELECTOR = '.pheroz-hero__canvas';
  const LOADER_SELECTOR = '.pheroz-loader';
  const LOADER_FILL_SELECTOR = '.pheroz-loader__bar-fill';
  const LOADER_TEXT_SELECTOR = '.pheroz-loader__text';
  const SCROLL_HINT_SELECTOR = '.pheroz-hero__scroll-hint';
  const DPR_CAP_MOBILE = 1.25; // Optimized for mobile memory usage
  const MOBILE_BREAKPOINT = 768;
  const SMOOTHING_FACTOR = 0.35; // Snappy response: eliminates drift when user stops scrolling

  /* ---------- State ---------- */
  let frames = [];
  let currentFrameIndex = -1;
  let isLoaded = false;
  let canvasEl, ctx;
  let heroEl, loaderEl, loaderFill, loaderText, scrollHint;
  let overlayGroups = [];
  let canvasWidth = 0;
  let canvasHeight = 0;

  // Smoothing state
  let targetProgress = 0;
  let currentProgress = 0;
  let renderRaf = null;
  let renderLayout = null; // Cache for layout math

  /* ---------- Overlay timeline ---------- */
  const OVERLAY_TIMELINE = []; // Custom logic can be added here if needed

  /* ---------- Init ---------- */
  function init() {
    heroEl = document.querySelector(HERO_SELECTOR);
    canvasEl = document.querySelector(CANVAS_SELECTOR);
    loaderEl = document.querySelector(LOADER_SELECTOR);
    loaderFill = document.querySelector(LOADER_FILL_SELECTOR);
    loaderText = document.querySelector(LOADER_TEXT_SELECTOR);
    scrollHint = document.querySelector(SCROLL_HINT_SELECTOR);

    if (!heroEl || !canvasEl) return;

    ctx = canvasEl.getContext('2d', { alpha: false }); // Opt-out of alpha for performance

    // Collect overlay groups
    overlayGroups = Array.from(document.querySelectorAll('.pheroz-hero__overlay-group'));

    // Detect device for frame loading
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const frameDataEl = isMobile
      ? (document.getElementById('pheroz-frame-data-mobile') || document.getElementById('pheroz-frame-data'))
      : document.getElementById('pheroz-frame-data');

    if (!frameDataEl) return;

    let frameUrls;
    try {
      frameUrls = JSON.parse(frameDataEl.textContent);
    } catch (e) {
      console.error('[PHEROZ] Failed to parse frame data:', e);
      return;
    }

    if (!frameUrls || frameUrls.length === 0) return;

    // Setup canvas size
    setupCanvas();

    // Preload frames
    preloadFrames(frameUrls);

    // Initial event listeners
    window.addEventListener('resize', debounce(setupCanvas, 200));
    window.addEventListener('scroll', onScroll, { passive: true });

    // Smooth scroll for CTAs
    document.querySelectorAll('[data-pheroz-scroll]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        const target = document.querySelector(el.getAttribute('href'));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------- Canvas Setup ---------- */
  function setupCanvas() {
    const rect = canvasEl.parentElement.getBoundingClientRect();
    let dpr = window.devicePixelRatio || 1;

    if (window.innerWidth <= MOBILE_BREAKPOINT && dpr > DPR_CAP_MOBILE) {
      dpr = DPR_CAP_MOBILE;
    }

    canvasWidth = rect.width;
    canvasHeight = rect.height;

    canvasEl.width = canvasWidth * dpr;
    canvasEl.height = canvasHeight * dpr;
    canvasEl.style.width = canvasWidth + 'px';
    canvasEl.style.height = canvasHeight + 'px';

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderLayout = null; // Invalidate cache on resize

    // Initial draw if already loaded
    if (isLoaded && currentFrameIndex >= 0) {
      drawFrame(currentFrameIndex);
    }
  }

  /* ---------- Preload Frames ---------- */
  function preloadFrames(urls) {
    const total = urls.length;
    let loaded = 0;

    urls.forEach((url, index) => {
      const img = new Image();
      img.onload = () => {
        // Use .decode() for smoother main-thread performance if available
        if ('decode' in img) {
          img.decode().then(() => {
            handleImageLoad(index, img);
          }).catch(() => {
            handleImageLoad(index, img);
          });
        } else {
          handleImageLoad(index, img);
        }
      };

      img.onerror = () => handleImageLoad(index, null);
      img.src = url;

      function handleImageLoad(idx, image) {
        loaded++;
        if (image) frames[idx] = image;
        updateLoader(loaded, total);
        if (loaded === total) onAllFramesLoaded();
      }
    });
  }

  /* ---------- Loader Update ---------- */
  function updateLoader(loaded, total) {
    const pct = Math.round((loaded / total) * 100);
    if (loaderFill) loaderFill.style.width = pct + '%';
    if (loaderText) loaderText.textContent = `${loaded} / ${total} — ${pct}%`;
  }

  /* ---------- All Frames Loaded ---------- */
  function onAllFramesLoaded() {
    isLoaded = true;
    if (loaderEl) loaderEl.classList.add('loaded');

    // Initial scroll calc
    onScroll();
    currentProgress = targetProgress; // Start without jump

    // Start continuous render loop
    startRenderLoop();
  }

  /* ---------- Scroll Handler ---------- */
  function onScroll() {
    if (!isLoaded) return;

    const rect = heroEl.getBoundingClientRect();
    const heroHeight = heroEl.offsetHeight;
    const viewportHeight = window.innerHeight;

    const scrolled = -rect.top;
    const scrollableDistance = heroHeight - viewportHeight;
    targetProgress = Math.max(0, Math.min(1, scrolled / scrollableDistance));

    // Instant hide for scroll hint if scrolled past threshold
    if (scrollHint) {
      if (targetProgress > 0.05) scrollHint.classList.add('hidden');
      else scrollHint.classList.remove('hidden');
    }
  }

  /* ---------- Render Loop (Maximum Fluidity) ---------- */
  function startRenderLoop() {
    function update() {
      // Lerp (Linear Interpolation) formula
      // current = current + (target - current) * factor
      const diff = targetProgress - currentProgress;

      // If the difference is negligible, we can stop drawing but we keep the loop reactive
      if (Math.abs(diff) > 0.0001) {
        currentProgress += diff * SMOOTHING_FACTOR;
      }

      const totalFrames = frames.length;
      const newIndex = Math.min(
        Math.floor(currentProgress * totalFrames),
        totalFrames - 1
      );

      // Only draw if the calculated frame index has actually changed
      if (newIndex !== currentFrameIndex && newIndex >= 0) {
        currentFrameIndex = newIndex;
        drawFrame(currentFrameIndex);
      }

      // Update overlays based on current (smoothed) progress
      updateOverlays(currentProgress);

      renderRaf = requestAnimationFrame(update);
    }

    renderRaf = requestAnimationFrame(update);
  }

  /* ---------- Draw Frame ---------- */
  function drawFrame(index) {
    const img = frames[index];
    if (!img || !img.naturalWidth) return;

    // Base background color
    ctx.fillStyle = '#0B0B0F';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (!renderLayout || renderLayout.canvasW !== canvasWidth || renderLayout.canvasH !== canvasHeight) {
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      const imgRatio = imgW / imgH;
      const canvasRatio = canvasWidth / canvasHeight;

      let drawW, drawH, drawX, drawY;
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

      if (isMobile) {
        // Mobile: Cover fit (fill screen)
        if (canvasRatio > imgRatio) {
          drawW = canvasWidth;
          drawH = drawW / imgRatio;
          drawX = 0;
          drawY = (canvasHeight - drawH) / 2;
        } else {
          drawH = canvasHeight;
          drawW = drawH * imgRatio;
          drawX = (canvasWidth - drawW) / 2;
          drawY = 0;
        }
      } else {
        // Desktop: Contain fit (prevent clipping)
        if (canvasRatio > imgRatio) {
          drawH = canvasHeight;
          drawW = drawH * imgRatio;
          drawX = (canvasWidth - drawW) / 2;
          drawY = 0;
        } else {
          drawW = canvasWidth;
          drawH = drawW / imgRatio;
          drawX = 0;
          drawY = (canvasHeight - drawH) / 2;
        }
      }
      renderLayout = { drawW, drawH, drawX, drawY, canvasW: canvasWidth, canvasH: canvasHeight };
    }

    ctx.drawImage(img, renderLayout.drawX, renderLayout.drawY, renderLayout.drawW, renderLayout.drawH);
  }

  /* ---------- Update Overlays ---------- */
  function updateOverlays(progress) {
    if (!OVERLAY_TIMELINE.length) return;
    OVERLAY_TIMELINE.forEach(entry => {
      const el = overlayGroups[entry.group];
      if (!el) return;
      if (progress >= entry.start && progress <= entry.end) el.classList.add('active');
      else el.classList.remove('active');
    });
  }

  /* ---------- Debounce ---------- */
  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /* ---------- Scroll Reveal (Intersection Observer) ---------- */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('.pheroz-reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    reveals.forEach(el => observer.observe(el));
  }

  /* ---------- Runtime ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initScrollReveal();
    });
  } else {
    init();
    initScrollReveal();
  }
})();
